/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { FancyAction } from 'src/app/common/fancy-firebase/base/FancyAction';
import { FancyActionableFirebase } from 'src/app/common/fancy-firebase/generics/FancyActionableFirebase';
import { PlanAccessList } from 'src/app/common/fancy-firebase/services/factories';
import { PlanAccess } from '../models/PlanAccess';
import { fbdbTime } from 'src/app/plain-javascript/background-scripts/fbdb-time';
import { UserIdTpData } from 'src/app/common/injectable-data/user-id';
import { DefaultSplitDataRenderType, PlanAccessLevel, IPlanAccessFields } from '@touchplan/common';

export class PlanAccessApi extends FancyActionableFirebase<PlanAccess, FancyAction, PlanAccessList> {
	constructor(
		public data: PlanAccessList,
		// BEGIN PLACEHOLDER REPLACE AS PART OF TP-6383
		private userIdTpData: UserIdTpData<string> // END PLACEHOLDER REPLACE AS PART OF TP-6383
	) {
		super(data);
	}

	/**
	 * Activates a plan by setting its access level to "live".
	 *
	 * @param planId - The unique identifier of the plan to activate.
	 * @param userId - The unique identifier of the user requesting the activation.
	 */
	public activatePlan(planId: string) {
		this.updatePlanAccessLevel(planId, PlanAccessLevel.live);
	}

	/**
	 * Locks a plan by setting its access level to "locked".
	 *
	 * @param planId - The unique identifier of the plan to lock.
	 * @param userId - The unique identifier of the user requesting the lock.
	 */
	public lockPlan(planId: string) {
		this.updatePlanAccessLevel(planId, PlanAccessLevel.locked);
	}

	/**
	 * Updates the access level of a specified plan, optionally overriding whether it should be excluded from the dashboard.
	 *
	 * @param planId - The unique identifier of the plan to update.
	 * @param planAccessLevel - The new access level for the plan. Defaults to `PlanAccessLevel.live`.
	 * @param excludeFromDashboardOverride - Optional. If provided, overrides the `excludeFromDashboard` setting for the plan.
	 *
	 * The method determines the `excludeFromDashboard` value by following this logic:
	 * - If the plan is archived, it prioritizes `true`.
	 * - If `excludeFromDashboardOverride` is provided, it takes precedence.
	 * - Otherwise, it uses the existing `excludeFromDashboard` value or defaults to `false` if not set.
	 *
	 * The method updates or adds the plan access data based on whether the plan already exists in the `syncList`. It also updates the metadata like `accessModifiedBy` and `accessModifiedAt`.
	 */
	public updatePlanAccessLevel(
		planId: string,
		planAccessLevel: PlanAccessLevel,
		excludeFromDashboardOverride?: boolean
	) {
		const existingPlanAccessItem = this.data.syncList.get(planId);

		// Prioritize true if the plan is archived, then the override if provided, otherwise use the existing value or default to false.
		const isArchived = planAccessLevel === PlanAccessLevel.archived;
		const currentExludeFromDashboard = existingPlanAccessItem?.view?.excludeFromDashboard ?? false;
		const excludeFromDashboard = isArchived
			? true
			: excludeFromDashboardOverride ?? currentExludeFromDashboard;

		const updatedPlanAccessData: Partial<IPlanAccessFields> = {
			excludeFromDashboard,
			accessLevel: planAccessLevel,
			// BEGIN PLACEHOLDER REPLACE AS PART OF TP-6383
			accessModifiedBy: this.userIdTpData.userIdSync,
			// END PLACEHOLDER REPLACE AS PART OF TP-6383
			accessModifiedAt: fbdbTime.syncTime,
		};
		if (existingPlanAccessItem) {
			this.localUpdate(planId, updatedPlanAccessData);
			this.persistData(existingPlanAccessItem, DefaultSplitDataRenderType.database);
		} else {
			this.addItem(updatedPlanAccessData, planId, true);
		}
	}
}
